import { useAtomValue } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import { useState } from 'react'
import { AnimateChangeInHeight } from '../../../rapid-ui'
import { layerAtomFamily, layersAtom, layerTreeAtom, selectedLayerIdAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type { Layer, LayerNode } from '../data/v1'
import { useDesignerActions } from '../use-designer-actions'
import {
	BackdropLayerInspector,
	ContentLayerInspector,
	DeleteLayerButton,
	DuplicateLayerButton,
	ImageLayerInspector,
	ShapeLayerInspector,
	SubstackLayerInspector,
	TextLayerInspector,
} from './layer-inspectors'

/** Walk the tree to find the path to `targetId`, returning ancestor IDs (excluding target). */
function findAncestorIds(nodes: LayerNode[], targetId: string, path: string[] = []): string[] | null {
	for (const node of nodes) {
		if (node.id === targetId) { return path }
		const found = findAncestorIds(node.children, targetId, [...path, node.id])
		if (found) { return found }
	}
	return null
}

function LayerTypeInspector({ layer }: { layer: Layer }) {
	switch (layer.type) {
		case 'content':
			return <ContentLayerInspector layer={layer} />
		case 'backdrop':
			return <BackdropLayerInspector layer={layer} />
		case 'substack':
			return <SubstackLayerInspector layer={layer} />
		case 'text':
			return <TextLayerInspector layer={layer} />
		case 'image':
			return <ImageLayerInspector layer={layer} />
		case 'shape':
			return <ShapeLayerInspector layer={layer} />
		default:
			return null
	}
}

function IdEditor({ currentId }: { currentId: string }) {
	const [draft, setDraft] = useState(currentId)
	const [error, setError] = useState<string | null>(null)
	const store = useCanvasStore()
	const allLayers = useAtomValue(layersAtom, { store })
	const { renameLayer } = useDesignerActions()

	const isDirty = draft !== currentId

	function handleSave() {
		const trimmed = draft.trim()
		if (!trimmed) {
			setError('ID cannot be empty')
			return
		}
		if (trimmed === currentId) { return }

		if (trimmed in allLayers) {
			setError('ID already exists')
			return
		}

		const success = renameLayer(currentId, trimmed)
		if (!success) {
			setError('Failed to rename')
			return
		}

		setError(null)
	}

	return (
		<label className="flex flex-col gap-1">
			<span className="text-[10px] font-medium tracking-wider text-grey-400 uppercase">ID</span>
			<div className="flex gap-1">
				<input
					type="text"
					value={draft}
					onChange={(e) => {
						setDraft(e.target.value)
						setError(null)
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && isDirty) { handleSave() }
					}}
					className="min-w-0 flex-1 rounded border border-grey-200 bg-grey-50 px-2 py-1 text-xs text-grey-800 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
				/>
				{isDirty && (
					<button
						onClick={handleSave}
						className="shrink-0 rounded bg-blue-500 px-2 py-1 text-[10px] font-medium text-white hover:bg-blue-600"
					>
						Save
					</button>
				)}
			</div>
			{error && <span className="text-[10px] text-red-500">{error}</span>}
		</label>
	)
}

function HiddenLayerWarning({ layerId }: { layerId: string }) {
	const store = useCanvasStore()
	const layer = useAtomValue(layerAtomFamily(layerId), { store })
	const layers = useAtomValue(layersAtom, { store })
	const layerTree = useAtomValue(layerTreeAtom, { store })
	const { updateLayer } = useDesignerActions()

	const isHidden = layer?.hidden
	const ancestorIds = findAncestorIds(layerTree, layerId) ?? []
	const inheritedHidden = ancestorIds.some((id) => layers[id]?.hidden)
	const effectivelyHidden = isHidden || inheritedHidden

	if (!effectivelyHidden) { return null }

	return (
		<div className="mb-2 rounded bg-yellow-50 px-3 py-1.5 text-[11px] text-yellow-700 flex items-center justify-between">
			{inheritedHidden
				? 'Hidden by a parent layer.'
				: 'This layer is hidden.'}
			{isHidden && !inheritedHidden && (
				<button
					type="button"
					onClick={() => updateLayer(layerId, { hidden: false })}
					className="rounded bg-yellow-200 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800 hover:bg-yellow-300"
				>
					Show
				</button>
			)}
		</div>
	)
}

function LockedLayerWarning({ layerId }: { layerId: string }) {
	const store = useCanvasStore()
	const layer = useAtomValue(layerAtomFamily(layerId), { store })
	const layers = useAtomValue(layersAtom, { store })
	const layerTree = useAtomValue(layerTreeAtom, { store })
	const { updateLayer } = useDesignerActions()

	const isLocked = layer?.locked
	const ancestorIds = findAncestorIds(layerTree, layerId) ?? []
	const inheritedLocked = ancestorIds.some((id) => layers[id]?.locked)
	const effectivelyLocked = isLocked || inheritedLocked

	if (!effectivelyLocked) { return null }

	return (
		<div className="mb-2 rounded bg-yellow-50 px-3 py-1.5 text-[11px] text-yellow-700 flex items-center justify-between">
			<p>
				{inheritedLocked
					? 'Locked by a parent layer.'
					: 'This layer is locked.'}
			</p>
			{isLocked && !inheritedLocked && (
				<button
					type="button"
					onClick={() => updateLayer(layerId, { locked: false })}
					className="rounded bg-yellow-200 px-1.5 py-0.5 text-[10px] font-medium text-yellow-800 hover:bg-yellow-300"
				>
					Unlock
				</button>
			)}
		</div>
	)
}

function LockedGate({ layerId, children }: { layerId: string; children: React.ReactNode }) {
	const store = useCanvasStore()
	const layer = useAtomValue(layerAtomFamily(layerId), { store })
	const layers = useAtomValue(layersAtom, { store })
	const layerTree = useAtomValue(layerTreeAtom, { store })

	const isLocked = layer?.locked
	const ancestorIds = findAncestorIds(layerTree, layerId) ?? []
	const inheritedLocked = ancestorIds.some((id) => layers[id]?.locked)
	const effectivelyLocked = isLocked || inheritedLocked

	if (effectivelyLocked) { return null }

	return <>{children}</>
}

export function Inspector() {
	const store = useCanvasStore()
	const selectedId = useAtomValue(selectedLayerIdAtom, { store })
	const selectedLayer = useAtomValue(layerAtomFamily(selectedId ?? ''), { store })

	return (
		<AnimateChangeInHeight duration={0.1} ease="linear" hideOverflow={false}>
			<AnimatePresence mode="wait" initial={false}>
				{!selectedId || !selectedLayer
					? (
						<motion.div key="empty" className="px-1 py-3 text-center text-xs text-grey-400">
							Select a layer to inspect
						</motion.div>
					)
					: (
						<motion.div
							key="inspector"
							layout
							transition={{ layout: { duration: 0 } }}
							className="border-t border-grey-100"
						>
							<div className="py-2">
								<HiddenLayerWarning layerId={selectedId} />
								<LockedLayerWarning layerId={selectedId} />
								<div className="flex items-start gap-2">
									<div className="min-w-0 flex-1">
										<IdEditor key={selectedId} currentId={selectedId} />
									</div>
									{/* TODO: move this to a better layout position */}
									<div className="flex shrink-0 gap-0.5 pt-5.5">
										<DuplicateLayerButton layerId={selectedId} />
										<DeleteLayerButton layerId={selectedId} />
									</div>
								</div>

								<LockedGate layerId={selectedId}>
									<div className="mt-3">
										<LayerTypeInspector layer={selectedLayer} />
									</div>
								</LockedGate>
							</div>
						</motion.div>
					)}
			</AnimatePresence>
		</AnimateChangeInHeight>
	)
}
